// Playwright Regression API — drops a job message into the
// playwright-regression queue via the same AppSync triggerAdAgencyJob
// mutation as research-deck / reporter / diagrams. The AppSync resolver
// routes "TRIGGER_PLAYWRIGHT_REGRESSION_JOB" to playwright-regression-queue
// → TriggerPlaywrightRegression Lambda → ecs.RunTask Fargate.
//
// On completion the Fargate task writes a PLAYWRIGHTREGRESSIONTICKET row
// to is-media-table (library-card shape: tenantId / entityType#entityId /
// JSON-stringified data field). The page polls that row via getLibraryCard
// to surface live status / final outcome / deck.pptx download URL.
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  TRIGGER_AD_AGENCY_JOB_MUTATION,
  GET_LIBRARY_CARD_QUERY,
} from "../queries/libraryCardQueries";
import { getAuthToken } from "../utils/authUtils";

const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// Expected payload shape:
//   { runId, tenantId, baseUrl?, specPath? }
// baseUrl defaults to https://x20-creative.com on the agent side; specPath
// defaults to the journey-snapshots spec. Phase 1 doesn't surface either
// in the FE form — the page sends just runId + tenantId so the bot tests
// the canonical site with the canonical spec.
export const triggerPlaywrightRegressionApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_PLAYWRIGHT_REGRESSION_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Fetch a PLAYWRIGHTREGRESSIONTICKET row via the shared library-card
// resolver. The BE writes all ticket fields inside a JSON-stringified
// `data` field (library-card convention); we parse it before handing
// back to the UI. Returns null if the row hasn't been written yet
// (happens briefly between mutation and trigger Lambda picking it up).
export const getPlaywrightRegressionTicketApi = async ({ tenantId, runId }) => {
  try {
    const { data } = await client.query({
      query: GET_LIBRARY_CARD_QUERY,
      variables: {
        input: {
          operationType: "GET_LIBRARY_CARD",
          tenantId,
          entityType: "PLAYWRIGHTREGRESSIONTICKET",
          entityId: runId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const card = data?.getLibraryCard;
    if (!card || !card.data) return null;
    return typeof card.data === "string" ? JSON.parse(card.data) : card.data;
  } catch (err) {
    const msg = String(err?.message || "");
    if (msg.includes("not found")) return null;
    throw err;
  }
};
